import { useMemo } from "react";
import { LoanSchemaDto, McaExpectedPaymentResult } from "dtos/loan.dto";
import { formatMoney } from "utils/functions";
import { ON_TRACK_TOLERANCE, Verdict, describeFlag, pctLabel } from "./constants";

export type ReconLoan = Pick<
  LoanSchemaDto,
  | "receivables"
  | "due_amount"
  | "payback_amount"
  | "funded_amount"
  | "funded_date"
  | "frequency"
  | "currency"
  | "expected_payment_breakdown"
>;

export interface ReconWarning {
  key: string;
  severity: "info" | "warning" | "error";
  title: string;
  body: string;
}

export interface ReconModel {
  result: McaExpectedPaymentResult | undefined;
  verdict: Verdict;
  pctFraction: number;
  expectedKnown: boolean;
  actualKnown: boolean;
  gapKnown: boolean;
  installmentAmountUsed: number | undefined;
  installmentAmountSuspect: boolean;
  warnings: ReconWarning[];
  blockingCount: number;
  headline: string;
}

const buildVerdict = (result: McaExpectedPaymentResult | undefined): Verdict => {
  if (!result) return "no_payload";
  if (result.coverage.monthsWithActualRevenue === 0 || result.expected.total <= 0) return "expected_unknown";
  if (result.actual.basis === "assumedFull") return "actual_unknown";
  if (result.actual.balance <= 0) return "paid_off";

  const share = result.variance.percentOfExpected;
  if (share === null || Math.abs(share) < ON_TRACK_TOLERANCE) return "on_track";
  return share > 0 ? "ahead" : "behind";
};

export const useMcaReconciliation = (loan: ReconLoan | undefined): ReconModel => {
  const result = loan?.expected_payment_breakdown;

  return useMemo(() => {
    const verdict = buildVerdict(result);
    const expectedKnown = verdict !== "no_payload" && verdict !== "expected_unknown";
    const actualKnown = Boolean(result) && result!.actual.basis !== "assumedFull";
    const gapKnown = expectedKnown && actualKnown;

    const rawPct = loan?.receivables ?? 0;
    const normalised = result ? result.flags.includes("PERCENTAGE_NORMALISED_FROM_WHOLE_NUMBER") : rawPct > 1;
    const pctFraction = normalised ? rawPct / 100 : rawPct;

    const installmentAmountUsed =
      result && result.actual.installmentsPaid > 0 ? result.actual.total / result.actual.installmentsPaid : undefined;

    const installmentsToPayback =
      installmentAmountUsed && installmentAmountUsed > 0 && loan?.payback_amount
        ? loan.payback_amount / installmentAmountUsed
        : undefined;

    const matchesOutstanding =
      installmentAmountUsed !== undefined &&
      loan?.due_amount !== undefined &&
      loan.due_amount > 0 &&
      Math.abs(installmentAmountUsed - loan.due_amount) < 1;

    const installmentAmountSuspect =
      Boolean(result) && (matchesOutstanding || (installmentsToPayback !== undefined && installmentsToPayback < 8));

    const warnings: ReconWarning[] = [];

    if (result) {
      if (loan?.due_amount !== undefined && Math.abs(result.actual.balance - loan.due_amount) > 1) {
        warnings.push({
          key: "BALANCE_DISAGREEMENT",
          severity: "error",
          title: "The two balances disagree",
          body: `The loan record carries ${formatMoney(loan.due_amount)} outstanding. Reconciliation derived ${formatMoney(
            result.actual.balance,
          )} instead. Trust the loan record until the payment ledger feeds this calculation.`,
        });
      }

      if (installmentAmountSuspect) {
        warnings.push({
          key: "INSTALLMENT_AMOUNT_SUSPECT",
          severity: "error",
          title: "The installment amount looks wrong",
          body: `The calculation ran on an installment of ${formatMoney(
            installmentAmountUsed,
          )}, which would clear ${formatMoney(loan?.payback_amount)} in under eight payments against ${
            result.window.installmentsElapsed
          } elapsed. Anything counted in installments is suppressed.`,
        });
      }

      result.flags.forEach((flag) => {
        const { severity, title, body } = describeFlag(flag);
        warnings.push({ key: flag, severity, title, body });
      });
    }

    const headline = (() => {
      if (!result) return "This advance has no reconciliation payload yet.";
      const { expected, actual, variance, coverage } = result;
      switch (verdict) {
        case "expected_unknown":
          return `Zero of the ${coverage.monthsInWindow} months in the window have revenue on file, so ${pctLabel(
            pctFraction,
          )} of receivables works out to nothing. Every figure that depends on it is a zero-fill.`;
        case "actual_unknown":
          return `${pctLabel(pctFraction)} of the revenue on file comes to ${formatMoney(
            expected.total,
          )} across ${coverage.monthsInWindow} months. Collections were assumed rather than observed, so the two sides aren't comparable yet.`;
        case "paid_off":
          return `${formatMoney(actual.total)} collected against ${formatMoney(
            expected.total,
          )} required by the percentage. Nothing outstanding.`;
        case "on_track":
          return `${formatMoney(actual.total)} collected against ${formatMoney(
            expected.total,
          )} required, inside a ${pctLabel(ON_TRACK_TOLERANCE, 0)} band.`;
        case "ahead":
        case "behind":
          return `${formatMoney(Math.abs(variance.amount))} ${
            verdict === "behind" ? "under" : "over"
          } what ${pctLabel(pctFraction)} of receivables required across ${coverage.monthsInWindow} months${
            variance.percentOfExpected !== null
              ? `, or ${pctLabel(Math.abs(variance.percentOfExpected), 1)} of the required amount`
              : ""
          }.`;
        default:
          return "";
      }
    })();

    return {
      result,
      verdict,
      pctFraction,
      expectedKnown,
      actualKnown,
      gapKnown,
      installmentAmountUsed,
      installmentAmountSuspect,
      warnings,
      blockingCount: warnings.filter((w) => w.severity !== "info").length,
      headline,
    };
  }, [loan, result]);
};
